import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="FridgeLife" className="w-10 h-10" />
              <h1 className="text-xl font-bold text-primary-green">FridgeLife</h1>
            </div>
            <Link to="/" className="flex items-center gap-2 px-4 py-2 text-sm text-primary-green font-medium hover:bg-primary-mint/50 rounded-lg transition-all">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last Updated: November 9, 2025</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Welcome to FridgeLife! By accessing or using our service, you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use our service.
              </p>
              <p className="text-gray-600 leading-relaxed">
                FridgeLife provides a food inventory management platform to help households track groceries, reduce waste, 
                and save money through smart reminders and analytics.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Account Registration</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                To use FridgeLife, you must create an account by providing accurate and complete information. You are 
                responsible for:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access</li>
                <li>Ensuring your email address is current and valid for notifications</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                You must be at least 13 years old to create an account. If you are under 18, you must have parental 
                or guardian consent to use our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Subscription Plans</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                FridgeLife offers two subscription plans:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Free Plan</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                  <li>30-day free trial</li>
                  <li>Maximum 15 items</li>
                  <li>Maximum 2 household members</li>
                  <li>5 barcode scans per month</li>
                  <li>Basic analytics</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Premium Plan ($4/month)</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                  <li>Unlimited items</li>
                  <li>Unlimited household members</li>
                  <li>Unlimited barcode scans</li>
                  <li>Advanced analytics</li>
                  <li>Priority support</li>
                </ul>
              </div>

              <p className="text-gray-600 leading-relaxed">
                Premium subscriptions automatically renew monthly. You may cancel at any time through your account settings. 
                Refunds are not provided for partial months of service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Use of Service</h2>
              <p className="text-gray-600 leading-relaxed mb-4">You agree to use FridgeLife only for lawful purposes. You agree NOT to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on the intellectual property rights of others</li>
                <li>Transmit malicious code, viruses, or harmful software</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use the service to spam or harass other users</li>
                <li>Scrape, crawl, or systematically extract data from our service</li>
                <li>Resell or redistribute our service without permission</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Household Sharing</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                FridgeLife allows you to share your household inventory with other users. When you invite members to your 
                household:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>All household members can view and edit items in the shared inventory</li>
                <li>You are responsible for managing household access and permissions</li>
                <li>The household owner can remove members at any time</li>
                <li>Subscription limits apply to the entire household, not individual members</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Accuracy</h2>
              <p className="text-gray-600 leading-relaxed">
                FridgeLife provides information about food products through barcode scanning and external databases. 
                While we strive for accuracy, we do not guarantee that product information, expiration dates, or 
                nutritional data is complete or error-free. You are responsible for verifying all information and 
                using your own judgment when consuming food products.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Notifications</h2>
              <p className="text-gray-600 leading-relaxed">
                FridgeLife sends email notifications about expiring items. You agree to receive these notifications 
                as part of using our service. We are not liable if notifications are delayed, not received, or if you fail to act on them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                All content, features, and functionality of FridgeLife, including but not limited to text, graphics, 
                logos, icons, software, and data compilations, are the exclusive property of FridgeLife and are 
                protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-gray-600 leading-relaxed">
                You retain ownership of the data you enter into FridgeLife. By using our service, you grant us a 
                license to use, store, and process your data to provide and improve our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Payment Terms</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Premium subscriptions are processed through Stripe. By subscribing, you authorize us to charge your 
                payment method on a recurring monthly basis. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Provide current, complete, and accurate payment information</li>
                <li>Update your payment information promptly if it changes</li>
                <li>Pay all charges incurred under your account</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                If payment fails, we may suspend or terminate your Premium access until payment is received.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Cancellation and Termination</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You may cancel your Premium subscription at any time through your account settings or the Stripe 
                billing portal. Upon cancellation:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>You will retain Premium access until the end of your current billing period</li>
                <li>No refunds will be provided for partial months</li>
                <li>Your account will automatically revert to the Free plan</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                We reserve the right to suspend or terminate your account if you violate these Terms of Service or 
                engage in fraudulent activity.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Disclaimer of Warranties</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                FRIDGELIFE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR 
                IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties that the service will be uninterrupted, secure, or error-free</li>
                <li>Warranties regarding the accuracy or completeness of content or data</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, FRIDGELIFE AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY 
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS 
                OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN 
                ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID 
                US IN THE 12 MONTHS PRECEDING THE CLAIM.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Indemnification</h2>
              <p className="text-gray-600 leading-relaxed">
                You agree to indemnify, defend, and hold harmless FridgeLife and its officers, directors, employees, 
                and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable 
                attorney fees) arising out of or related to your use of the service, violation of these Terms, or 
                infringement of any rights of another party.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                We reserve the right to modify these Terms of Service at any time. If we make material changes, we 
                will notify you by email or through the service. Your continued use of FridgeLife after such notice 
                constitutes acceptance of the modified terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Governing Law</h2>
              <p className="text-gray-600 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States, 
                without regard to its conflict of law provisions. Any disputes arising from these Terms or your use 
                of FridgeLife shall be resolved in the courts of the United States.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Entire Agreement</h2>
              <p className="text-gray-600 leading-relaxed">
                These Terms of Service, together with our Privacy Policy, constitute the entire agreement between 
                you and FridgeLife regarding your use of the service and supersede all prior agreements and 
                understandings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Contact Information</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-primary-mint/50 rounded-lg p-6">
                <p className="text-gray-700 font-medium mb-1">FridgeLife Support</p>
                <p className="text-gray-600">Email: fridgelife@yahoo.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <footer className="bg-white border-t border-gray-200 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <Link to="/terms" className="text-primary-green hover:text-primary-green/80 font-medium text-sm">
              Terms of Service
            </Link>
            <span className="text-gray-300">•</span>
            <Link to="/privacy" className="text-primary-green hover:text-primary-green/80 font-medium text-sm">
              Privacy Policy
            </Link>
          </div>
          <p className="text-gray-600 text-sm">Built with 💚 by FridgeLife • Helping reduce food waste, one fridge at a time</p>
        </div>
      </footer>
    </div>
  );
};
